/**
 * DesignationBadge Component
 *
 * Renders a tier-appropriate badge pill for feature maturity designation.
 * Used for inline/compact UI elements (cards, buttons, navigation items, table rows).
 *
 * Color scheme per tier:
 *   coming_soon  - Gray background, muted text
 *   prototype    - Purple background, purple text
 *   alpha        - Yellow background, amber text
 *   demo         - Blue background, blue text
 *
 * Production features (no designation) render nothing.
 *
 * @see https://gitlab.com/smart-assets.io/gitlab-profile/-/blob/master/docs/designs/feature-maturity-designation-system.md
 */
import React from "react";
import type { FeatureDesignation } from "../../types/designation";
import { DESIGNATION_STYLES } from "../../types/designation";

export interface DesignationBadgeProps {
  /** The feature designation tier. Null/undefined = production (no badge). */
  designation?: FeatureDesignation | null;
  /** Optional size variant */
  size?: "sm" | "md";
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CLASSES = {
  sm: "px-1.5 py-0.5 text-[10px]",
  md: "px-2 py-0.5 text-xs",
};

const DesignationBadge: React.FC<DesignationBadgeProps> = ({
  designation,
  size = "sm",
  className = "",
}) => {
  if (!designation) return null;

  const style = DESIGNATION_STYLES[designation];

  return (
    <span
      data-testid={`designation-badge-${designation}`}
      className={`
        inline-flex items-center font-semibold uppercase tracking-wide rounded
        ${SIZE_CLASSES[size]}
        ${style.bg}
        ${style.text}
        ${className}
      `}
    >
      {style.label}
    </span>
  );
};

export default DesignationBadge;
